import React, { useEffect, useState } from 'react';
import './HomeWeather.scss';

const describeWeather = code => code === 0 ? 'Despejado' : code <= 3 ? 'Parcialmente nublado' : code <= 48 ? 'Niebla' : code <= 67 ? 'Lluvia' : code <= 77 ? 'Nieve' : code <= 82 ? 'Chubascos' : code <= 86 ? 'Nieve' : code >= 95 ? 'Tormenta' : 'Variable';
const hourInZone = (date, zone) => Number(new Intl.DateTimeFormat('en-GB', { hour: '2-digit', hourCycle: 'h23', timeZone: zone }).format(date));
const greetingFor = hour => hour < 12 ? 'Buenos días' : hour < 19 ? 'Buenas tardes' : 'Buenas noches';
const weatherIcon = code => code == null ? '☀' : code <= 1 ? '☀' : code <= 3 ? '⛅' : code <= 48 ? '☁' : code <= 67 ? '🌧' : code <= 77 ? '❄' : code <= 82 ? '🌦' : code <= 86 ? '❄' : '⛈';

export default function HomeWeather() {
  const [now, setNow] = useState(() => new Date());
  const [data, setData] = useState(null);
  const [location, setLocation] = useState('');
  const [status, setStatus] = useState('Cargando clima…');
  const [city, setCity] = useState('');
  const zone = data?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const hour = hourInZone(now, zone);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    let cancelled = false;
    let refreshId;
    let chosen = null;
    const load = async (lat, lon, label) => {
      try {
        const endpoint = 'https://api.open-meteo.com/v1/forecast?latitude=' + lat + '&longitude=' + lon + '&current=temperature_2m,relative_humidity_2m,weather_code,wind_speed_10m&daily=sunrise,sunset&timezone=auto&forecast_days=1';
        const res = await fetch(endpoint);
        if (!res.ok) throw new Error('weather unavailable');
        const result = await res.json();
        if (cancelled) return;
        setData(result);
        setStatus('');
        setLocation(label);
        chosen = { lat, lon, label };
      } catch (_) {
        if (!cancelled) setStatus('Clima no disponible. Puedes consultar por ciudad.');
      }
    };
    const approximate = async () => {
      if (cancelled || chosen) return;
      setStatus('Detectando ubicación aproximada…');
      try {
        const res = await fetch('https://ipwho.is/', { signal: AbortSignal.timeout(9000) });
        if (!res.ok) throw new Error('location unavailable');
        const place = await res.json();
        if (cancelled || chosen) return;
        if (!place.success || !Number.isFinite(place.latitude) || !Number.isFinite(place.longitude)) throw new Error('no coordinates');
        await load(place.latitude, place.longitude, (place.city || place.region || 'Tu zona') + ' (aprox.)');
      } catch (_) {
        if (!cancelled && !chosen) setStatus('No se pudo detectar la ubicación. Consulta por ciudad.');
      }
    };
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        p => {
          if (cancelled) return;
          load(p.coords.latitude, p.coords.longitude, 'Ubicación actual');
        },
        () => approximate(),
        { enableHighAccuracy: false, timeout: 7000, maximumAge: 600000 }
      );
    } else {
      approximate();
    }
    refreshId = setInterval(() => {
      if (chosen) load(chosen.lat, chosen.lon, chosen.label);
    }, 15 * 60 * 1000);
    return () => { cancelled = true; clearInterval(refreshId); };
  }, []);

  const searchCity = async event => {
    event.preventDefault();
    if (!city.trim()) return;
    setStatus('Buscando ciudad…');
    try {
      const response = await fetch('https://geocoding-api.open-meteo.com/v1/search?name=' + encodeURIComponent(city.trim()) + '&count=1&language=es');
      if (!response.ok) throw new Error('lookup failed');
      const result = await response.json();
      const place = result.results?.[0];
      if (!place) { setStatus('No se encontró esa ciudad'); return; }
      const endpoint = 'https://api.open-meteo.com/v1/forecast?latitude=' + place.latitude + '&longitude=' + place.longitude + '&current=temperature_2m,relative_humidity_2m,weather_code,wind_speed_10m&daily=sunrise,sunset&timezone=auto&forecast_days=1';
      const weatherResponse = await fetch(endpoint);
      if (!weatherResponse.ok) throw new Error('weather failed');
      setData(await weatherResponse.json());
      setLocation(place.name + (place.admin1 ? ', ' + place.admin1 : ''));
      setStatus('');
    } catch (_) { setStatus('No se pudo consultar el clima'); }
  };

  const current = data?.current;
  const sunrise = data?.daily?.sunrise?.[0];
  const sunset = data?.daily?.sunset?.[0];
  // Open-Meteo daily timestamps are wall-clock times in the returned timezone.
  const formatLocalDaily = value => {
    if (!value) return '--';
    const match = value.match(/T(\d{2}):(\d{2})/);
    if (!match) return '--';
    const h = Number(match[1]);
    return (h % 12 || 12) + ':' + match[2] + (h < 12 ? ' AM' : ' PM');
  };
  const minutesOf = value => {
    const m = value?.match(/T(\d{2}):(\d{2})/);
    return m ? Number(m[1]) * 60 + Number(m[2]) : null;
  };
  const sunriseMin = minutesOf(sunrise);
  const sunsetMin = minutesOf(sunset);
  const clock = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: zone }).format(now).split(':');
  const minute = Number(clock[0]) * 60 + Number(clock[1]);
  const daytime = sunriseMin != null && sunsetMin != null ? minute >= sunriseMin && minute <= sunsetMin : hour >= 6 && hour < 19;
  const progress = sunriseMin != null && sunsetMin != null && sunsetMin > sunriseMin ? Math.max(0, Math.min(1, (minute - sunriseMin) / (sunsetMin - sunriseMin))) : Math.max(0, Math.min(1, (minute - 360) / 780));

  const nightProgress = minute < (sunriseMin ?? 360) ? minute / Math.max(1, sunriseMin ?? 360) : (minute - (sunsetMin ?? 1140)) / Math.max(1, 1440 - (sunsetMin ?? 1140));
  const celestialProgress = daytime ? progress : Math.max(0, Math.min(1, nightProgress));

  return (
    <div className={'home-weather ' + (daytime ? 'home-weather--day' : 'home-weather--night')}>
      <div className="home-weather__shade" />
      <div className="home-weather__celestial" style={{ left: (8 + celestialProgress * 80) + '%', top: (24 - Math.sin(celestialProgress * Math.PI) * 12) + '%' }} aria-hidden="true">{daytime ? '☀' : '☾'}</div>
      <div className="home-weather__times">
        <div><span aria-hidden="true">☀</span><strong>{formatLocalDaily(sunrise)}</strong><small>Amanecer</small></div>
        <div><span aria-hidden="true">◉</span><strong>{formatLocalDaily(sunset)}</strong><small>Atardecer</small></div>
      </div>
      <div className="home-weather__greeting">
        <h1>¡HOLA!</h1>
        <h2>{greetingFor(hour)}</h2>
        <p>Que tengas un excelente día.</p>
      </div>
      <div className="home-weather__forecast" aria-live="polite">
        <div className="home-weather__place">⌖ {location || 'Clima de tu ubicación'}</div>
        {current ? <>
          <div className="home-weather__temperature"><span>{weatherIcon(current.weather_code)}</span><strong>{Math.round(current.temperature_2m)}°C</strong></div>
          <div className="home-weather__condition">{describeWeather(current.weather_code)}</div>
          <div className="home-weather__metrics">
            <div>Humedad <strong>{current.relative_humidity_2m}%</strong></div>
            <div>Viento <strong>{Math.round(current.wind_speed_10m)} km/h</strong></div>
          </div>
        </> : <div className="home-weather__status">{status}</div>}
        {!current && <form className="home-weather__city" onSubmit={searchCity}><label htmlFor="home-weather-city">Consultar por ciudad</label><div><input id="home-weather-city" value={city} onChange={event => setCity(event.target.value)} placeholder="Ciudad" aria-label="Ciudad para consultar clima" /><button type="submit">Buscar</button></div></form>}
      </div>
    </div>
  );
}
